import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import PropTypes from "prop-types";
import LoadingOverlay from "../../../../../Shared/LoadingOverlay";
import ChartEmptyState from "../../../../../Shared/chartEmptyState";
import { CHART_IDS, VENDOR_STATS_CHART_LIMITS } from "../../../../../../config/featureConfig";
import vendorStatsService from "../../../../../../services/vendorStatsService";
import TopClickedProductsChartCard from "./TopClickedProductsChartCard";
import { useDashboardChartsDate } from "../../context/dashboardChartsDateContext";

const TopClickedProductsChart = ({ embedInGrid = false, onOpenInTabView }) => {
  const { customRange } = useDashboardChartsDate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const { grid: gridLimit, list: listViewLimit } = VENDOR_STATS_CHART_LIMITS[CHART_IDS.TOP_PRODUCTS_BY_CLICK];
  const fetchLimit = embedInGrid ? gridLimit : listViewLimit;

  const maxClicks = useMemo(() => {
    const m = Math.max(0, ...items.map((i) => Number(i.clickCount) || 0));
    return m > 0 ? m : 1;
  }, [items]);

  const runFetch = useCallback(async (payload) => {
    setLoading(true);
    try {
      const res = await vendorStatsService.getTopProductsByClick(payload);
      setItems(res.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const payload = { limit: fetchLimit };
    if (customRange?.start && customRange?.end) {
      payload.startDate = customRange.start.format("YYYY-MM-DD");
      payload.endDate = customRange.end.format("YYYY-MM-DD");
    }
    runFetch(payload);
  }, [customRange, embedInGrid, fetchLimit, runFetch]);

  const hasClickData = useMemo(() => items.some((i) => Number(i.clickCount) > 0), [items]);

  const visibleItems = useMemo(() => {
    if (embedInGrid) return items.slice(0, gridLimit);
    return items;
  }, [embedInGrid, gridLimit, items]);

  return (
    <Box sx={{ position: "relative", minHeight: 200 }}>
      <LoadingOverlay open={loading} />
      <Stack spacing={2}>
        <Stack spacing={1.25}>
          {!loading && !hasClickData ? (
            <ChartEmptyState minHeight={260} />
          ) : !loading ? (
            visibleItems.map((item, idx) => {
              const cardKey = `${item.catalogListId ?? ""}-${item.productListId ?? idx}`;
              return (
                <TopClickedProductsChartCard
                  key={cardKey}
                  item={item}
                  maxClicks={maxClicks}
                  compact={embedInGrid}
                  onActivate={embedInGrid ? onOpenInTabView : undefined}
                />
              );
            })
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};

TopClickedProductsChart.propTypes = {
  embedInGrid: PropTypes.bool,
  onOpenInTabView: PropTypes.func,
};

export default TopClickedProductsChart;
